import { atom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { atomWithRefresh } from 'infrastructure/state/atomWithRefresh'
import { uploadCourse } from './Utility'
import { byStringFieldSort } from '../Helpers'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { useCallback } from 'react'
import { ICourseNode } from './IngestedTree'
import { doIngestionQueries } from '../../providers/ingestion'
import { IQueryInfo } from '../../providers/mutations'
import { AwsContentItem, AwsListResult } from 'shared/types/ingestion'

export type NewTreeData = {
  title: string
  id: number
  tree_id: string
  tree_version: string
}

export const newTreesAtom = atom<NewTreeData[]>([])

export const partnerIdAtom = atom('')

export const sourceFilesAtom = atomWithRefresh<Promise<AwsContentItem[]>>(
  async (get) => {
    const partnerId = get(partnerIdAtom)
    if (!partnerId) return []
    return queryFiles(partnerId, 'sourceFiles')
  },
)

// filters out any files that have a zipped version
export const filteredSourceFilesAtom = atom(
  async (get) => {
    const files = await get(sourceFilesAtom)
    return files.filter((f) => !files.find(({ key }) => key === `${f.key}.zip`))
  },
  (get, set) => {
    set(sourceFilesAtom)
  },
)

export const ingestedFilesAtom = atomWithRefresh(async (get) => {
  const partnerId = get(partnerIdAtom)
  if (!partnerId) return []
  const fileFilter = (f: AwsContentItem) =>
    !!f.key && !f.key.includes(partnerId)
  const ingested = await queryFiles(partnerId, 'ingestedFiles')
  return ingested
    .filter(fileFilter)
    .sort(byStringFieldSort('last_modified'))
    .reverse()
})

export function useUploadCourse() {
  const partnerId = useAtomValue(partnerIdAtom)
  const refresh = useSetAtom(ingestedFilesAtom)
  return useCallback(
    async (course: ICourseNode, optionalName?: string) => {
      await uploadCourse(partnerId, course, optionalName)
      refresh()
    },
    [partnerId, refresh],
  )
}

function queryInfoForFiles(partnerKey: string, fileKey: string): IQueryInfo {
  return {
    resourceData: {
      key: fileKey,
      fields: ['name', { contents: ['key', 'URL', 'last_modified'] }],
    },
    params: {
      partner_key: partnerKey,
    },
  }
}

async function queryFiles(
  partnerKey: string,
  fileKey: 'ingestedFiles' | 'sourceFiles',
): Promise<AwsContentItem[]> {
  const query = queryInfoForFiles(partnerKey, fileKey)
  const { data } = await doIngestionQueries<AwsListResult>([query], true)
  return data[fileKey].contents
}
